/**
 * Build the SessionStartConfig payload that apps/agent-runtime expects
 * over its `/ws` gateway. v1 of the runtime accepts the entire agent
 * group definition inline (it doesn't need anything seeded in its own
 * Mongo), so the testing harness can drive smoke tests against the
 * runtime without admin-side mirror data.
 *
 * The shape mirrors what `apps/agent-runtime/public/index.html`'s
 * `buildConfig()` sends — kept here as the single source of truth for
 * the harness so we don't have to keep two copies in sync.
 *
 * Caller supplies tenant identity + a few practice-flavored overrides
 * (variables surfaced as {{placeholders}} in prompts). Everything else
 * uses the same defaults the agent-runtime test client ships with.
 */

// `text_model` is the actual Gemini model id the chain controller asks the
// provider for (e.g. "gemini-2.5-flash"). `voice_model` is a family hint
// the chain layer reads to pick which provider+streaming mode to use —
// "chain-gemini" / "chain-openai" / "chain-deepseek". Don't conflate them.
const DEFAULT_TEXT_MODEL = "gemini-2.5-flash";
const DEFAULT_VOICE_FAMILY = "chain-gemini";
const DEFAULT_LIVE_MODEL = "gemini-2.5-flash-preview-native-audio-dialog";

/**
 * @param {object} opts
 * @param {string} opts.tenant           agent-runtime tenant key (matches `x-tenant-key` header)
 * @param {string} opts.subTenant        sub-tenant key inside that tenant
 * @param {string} [opts.tenantGroup]    routing-cache group; falls back to "default"
 * @param {"text"|"voice"} [opts.callMode]
 * @param {string} [opts.textModel]      "chain-gemini" | "chain-openai" | etc.
 * @param {string} [opts.liveModel]      Gemini-Live model id (voice only)
 * @param {string} [opts.voice]          voice name (voice only)
 * @param {string} [opts.languageCode]   BCP-47 (e.g., "en-US")
 * @param {object} [opts.variables]      placeholder bag merged onto defaults
 * @param {object} [opts.initialContext] optional pre-known userId / intent
 */
export function buildAgentRuntimeConfig(opts) {
  const {
    tenant,
    subTenant,
    tenantGroup = "default",
    callMode = "text",
    textModel = DEFAULT_TEXT_MODEL,
    liveModel = DEFAULT_LIVE_MODEL,
    voice = "Puck",
    languageCode = "en-US",
    variables = {},
    initialContext,
  } = opts || {};

  if (!tenant || !subTenant) {
    throw new Error("buildAgentRuntimeConfig: tenant and subTenant are required");
  }

  // For text mode, voice_model is the chain-family hint (e.g. "chain-gemini")
  // that signals to the chain controller which provider to route through.
  // For voice mode, voice_model is the actual Gemini Live model id.
  const voiceModel = callMode === "voice" ? liveModel : DEFAULT_VOICE_FAMILY;

  const config = {
    tenant,
    subTenant,
    tenantGroup,
    callMode,
    agents: [
      {
        agent_id: "greeter",
        name: "Greeter",
        description: "Welcomes the caller",
        type: "greeter",
        color: "#1f6feb",
        icon: "G",
        modes: ["inbound", "outbound"],
        handoff_mode: "lazy",
        tools: ["echo", "now"],
        triggers: [],
        linked_protocols: [],
        version: 1,
        enabled: true,
        seeded_from: null,
      },
      {
        agent_id: "scheduler",
        name: "Scheduler",
        description: "Books or changes appointments",
        type: "task",
        color: "#3fb950",
        icon: "S",
        modes: ["inbound", "outbound"],
        handoff_mode: "lazy",
        tools: ["lookup_appointments", "list_appointment_types", "get_available_slots", "schedule_appointment", "reschedule_appointment", "cancel_appointment"],
        triggers: [
          { type: "keyword", pattern: "schedule", priority: 10 },
          { type: "keyword", pattern: "appointment", priority: 10 },
          { type: "keyword", pattern: "book", priority: 20 },
          { type: "keyword", pattern: "reschedule", priority: 10 },
        ],
        linked_protocols: [],
        version: 1,
        enabled: true,
        seeded_from: null,
      },
      {
        agent_id: "triage",
        name: "Triage",
        description: "Triages symptoms",
        type: "task",
        color: "#d29922",
        icon: "T",
        modes: ["inbound", "outbound"],
        handoff_mode: "lazy",
        tools: ["get_triage_protocol", "list_triage_protocols", "notify_practice", "log_symptom_assessment"],
        triggers: [
          { type: "keyword", pattern: "pain", priority: 5 },
          { type: "keyword", pattern: "symptom", priority: 5 },
          { type: "keyword", pattern: "sick", priority: 10 },
        ],
        linked_protocols: [],
        version: 1,
        enabled: true,
        seeded_from: null,
      },
      {
        agent_id: "closer",
        name: "Closer",
        description: "Wraps up the call",
        type: "closer",
        color: "#bc8cff",
        icon: "C",
        modes: ["inbound", "outbound"],
        handoff_mode: "lazy",
        tools: ["end_call"],
        triggers: [],
        linked_protocols: [],
        version: 1,
        enabled: true,
        seeded_from: null,
      },
    ],
    llmConfig: {
      provider: "chain",
      voice_model: voiceModel,
      text_model: textModel,
      voice,
      language_code: languageCode,
      temperature: 0.2,
      max_output_tokens: 512,
      safety_level: "default",
      max_call_duration: 600,
      timeout_warning_at: 540,
      voice_pacing_instructions: "",
      handoff_mode: "lazy",
      noise_suppression: false,
      mic_noise_threshold: 0.003,
      twilio_bwe_enabled: false,
      twilio_bwe_frame_size: 320,
      deepfilternet_enabled: false,
      agc_enabled: false,
      agc_target_dbfs: -20,
      agc_noise_floor_dbfs: -45,
      agc_limiter_dbfs: -1,
      agc_attack_ms: 10,
      agc_release_ms: 800,
      agc_max_gain_db: 20,
      phone_vad_enabled: true,
      phone_vad_open_threshold: 0.008,
      phone_vad_close_threshold: 0.003,
      phone_vad_hangover_ms: 200,
      twilio_playback_rate: 0.82,
      twilio_adaptive_age_enabled: false,
      twilio_adaptive_trouble_enabled: false,
      twilio_adaptive_min_rate: 0.7,
      twilio_adaptive_trouble_step: 0.04,
      silence_reprompt: { enabled: false, timeout_seconds: 15, text: "Are you still there?", max_attempts: 2 },
      filter_llm: { enabled: false, model: "gemini-2.5-flash-lite", mode: "rewrite", instructions: "", apply_to_agents: [], timeout_ms: 2000 },
    },
    systemTools: {
      auto_close_call: true,
      language_detection: false,
      barge_in: true,
      active_listening: true,
      warm_handoff: false,
      live_transfer: false,
      voicemail_awareness: false,
    },
    variables: {
      practice_name: "Test Practice",
      practice_short_name: "Test",
      practice_phone: "555-100-1000",
      after_hours_phone: "555-100-1001",
      practice_address: "100 Test St",
      practice_city_state: "Testville, CA",
      clinic_hours: "Mon-Fri 9am-5pm",
      agent_persona_name: "Sandy",
      agent_persona_description: "care coordinator",
      agent_gender: "neutral",
      timezone: "America/Los_Angeles",
      default_language: "English",
      brand_voice: "concise and friendly",
      ...variables,
    },
    prompts: {
      scheduler: {
        agent_id: "scheduler",
        content: "You are the scheduling agent for {{practice_name}}. Help the caller book or change an appointment. When done, call complete_task with a summary.",
        version: 1,
        is_draft: false,
        is_live: true,
        metadata: { name: "Scheduler" },
        seeded_from: null,
        created_at: new Date().toISOString(),
        created_by: "testing-harness",
      },
      triage: {
        agent_id: "triage",
        content: "You are the triage agent. Ask about symptoms; when you can triage, call complete_task with urgency.",
        version: 1,
        is_draft: false,
        is_live: true,
        metadata: { name: "Triage" },
        seeded_from: null,
        created_at: new Date().toISOString(),
        created_by: "testing-harness",
      },
    },
    orchestration: {
      prompt_mode: "form",
      greeting_style: "warm",
      verification: { required: false, max_attempts: 3, on_fail: "stay_on_orchestrator" },
      intents: [
        { category: "schedule", agent: "scheduler", description: "scheduling-related" },
        { category: "symptom", agent: "triage", description: "symptom-related" },
      ],
      task_queue: { priority_order: ["resume_paused", "next_pending", "deferred_review"] },
      end_of_call: { review_deferred_tasks: false, close_after: true, pre_close_steps: [] },
      fallback: { on_no_match: "stay_on_orchestrator", on_tool_error: "auto" },
      prompt: "",
      custom_prompt: "",
      tools: [],
    },
    postCallActions: [
      { id: "summary", name: "Summary", description: "One-paragraph summary", order: 0, enabled: true, prompt: "Summarize the call in one paragraph." },
    ],
    postCallSettings: { mode: "single", model: textModel },
  };

  if (initialContext) config.initialContext = initialContext;
  return config;
}
